import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row, Col, Card, Table, Button, Input, Tag, Space, Modal,
  notification, Typography, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SendOutlined,
  ReloadOutlined, SearchOutlined, FilePdfOutlined, FileImageOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { listReports, deleteReport } from './monthlyReportService';

const { confirm } = Modal;
const { Title } = Typography;

const STATUS_COLOR = { draft: 'default', sent: 'green' };

export default function MonthlyReportList() {
  const history = useHistory();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const fetchList = async (page = 1, pageSize = 20, s = search) => {
    setLoading(true);
    try {
      const res = await listReports({ page, limit: pageSize, search: s });
      if (res.status) {
        setData(res.result.map((r, i) => ({ ...r, key: r.id, sno: (page - 1) * pageSize + i + 1 })));
        setPagination({ current: page, pageSize, total: res.pagination?.total || 0 });
      }
    } catch (err) {
      notification.error({ message: 'Failed to load reports', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []); // eslint-disable-line

  const handleDelete = (id) => {
    confirm({
      title: 'Delete this report?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete', okType: 'danger',
      onOk: async () => {
        try {
          await deleteReport(id);
          notification.success({ message: 'Report deleted' });
          fetchList(pagination.current, pagination.pageSize);
        } catch (err) {
          notification.error({ message: 'Delete failed', description: err.message });
        }
      },
    });
  };

  const columns = [
    { title: '#', dataIndex: 'sno', width: 60, align: 'center' },
    {
      title: 'Title', dataIndex: 'title',
      render: (v, r) => {
        const isImage = r.file_url && /\.(jpg|jpeg|png|gif|webp)$/i.test(r.file_url);
        return (
          <Space>
            {isImage
              ? <FileImageOutlined style={{ color: '#7a0000' }} />
              : <FilePdfOutlined style={{ color: '#ff4d4f' }} />}
            <span style={{ fontWeight: 500 }}>{v}</span>
            {!r.file_url && <Tag color="warning" style={{ fontSize: 11 }}>No File</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Report Month', dataIndex: 'report_month', width: 140,
      render: (v) => {
        if (!v) return '-';
        const [yr, mo] = v.split('-');
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${months[parseInt(mo) - 1]} ${yr}`;
      },
    },
    { title: 'Template', dataIndex: 'template_name', width: 180 },
    {
      title: 'Status', dataIndex: 'send_status', width: 100,
      render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{(v || 'draft').toUpperCase()}</Tag>,
    },
    {
      title: 'Last Sent', dataIndex: 'last_sent_at', width: 150,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
    },
    {
      title: 'Actions', key: 'actions', align: 'center', width: 220,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit details / Upload File">
            <Button
              size="small" icon={<EditOutlined />}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
              onClick={() => history.push(`/monthly-reports/edit/${record.id}`)}
            >Edit</Button>
          </Tooltip>
          <Tooltip title={record.file_url ? 'Send to members' : 'Upload file first'}>
            <Button
              size="small" icon={<SendOutlined />}
              style={{ color: '#52c41a', borderColor: '#52c41a' }}
              disabled={!record.file_url}
              onClick={() => history.push(`/monthly-reports/send/${record.id}`)}
            >Send</Button>
          </Tooltip>
          <Button size="small" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={<Title level={4} style={{ margin: 0, color: '#fff' }}>Monthly Reports</Title>}
            headStyle={{ borderBottom: '1.5px solid #e2e8f0' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <Input.Search
                placeholder="Search reports..."
                allowClear
                prefix={<SearchOutlined />}
                style={{ width: 260 }}
                onSearch={(v) => { setSearch(v); fetchList(1, pagination.pageSize, v); }}
              />
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => fetchList()}>Refresh</Button>
                <Button
                  icon={<PlusOutlined />}
                  style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
                  onClick={() => history.push('/monthly-reports/add')}
                >
                  New Report
                </Button>
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={data}
              loading={loading}
              rowKey="id"
              scroll={{ x: 900 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (t) => `Total ${t} reports`,
                onChange: (page, pageSize) => fetchList(page, pageSize),
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
